import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import { ArrowDownCircle, Unlink, Loader2, AlertCircle } from "lucide-react";

export default function BlueprintSubscriberCard({ subscription }) {
    const { t } = useTranslation();
    const [updateAvailable, setUpdateAvailable] = useState(null);
    const [policy, setPolicy] = useState(subscription?.updatePolicy);

    const { call: checkUpdate } = useBluelagoon({ method: "checkSubscriptionUpdate" });
    const { call: applyUpdate, isLoading: applyingUpdate } = useBluelagoon({
        method: "applySubscriptionUpdate",
    });
    const { call: updatePolicy, isLoading: updatingPolicy } = useBluelagoon({
        method: "updateSubscriptionPolicy",
    });
    const { call: eject, isLoading: ejecting } = useBluelagoon({
        method: "ejectSubscription",
    });

    const fetchUpdateStatus = useCallback(async () => {
        if (!subscription?.id || policy === "EJECTED") return;
        const res = await checkUpdate({ id: subscription.id });
        if (res?.data) setUpdateAvailable(res.data);
    }, [subscription?.id, policy]);

    useEffect(() => {
        fetchUpdateStatus();
    }, [fetchUpdateStatus]);

    const handleApplyUpdate = async () => {
        const res = await applyUpdate({ id: subscription.id });
        const result = res?.data;
        if (result?.applied) {
            toast.success(t("subscriptions.updateApplied", { version: result.version }));
            setUpdateAvailable((prev) => ({ ...prev, hasUpdate: false }));
        }
    };

    const handlePolicyChange = async (newPolicy) => {
        const res = await updatePolicy({ id: subscription.id, updatePolicy: newPolicy });
        if (res?.data) {
            setPolicy(newPolicy);
            toast.success(t("subscriptions.policyChanged", { policy: newPolicy }));
        }
    };

    const handleEject = async () => {
        if (!window.confirm(t("subscriptions.ejectConfirm"))) return;
        const res = await eject({ id: subscription.id });
        if (res?.data) {
            setPolicy("EJECTED");
            toast.success(t("subscriptions.ejected"));
        }
    };

    if (policy === "EJECTED") {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <Unlink className="w-4 h-4" />
                    <span className="text-sm">{t("subscriptions.ejectedMessage")}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">{t("subscriptions.title")}</h3>
                <Badge variant="outline">v{subscription?.subscribedVersion || 0}</Badge>
            </div>
            {updateAvailable?.hasUpdate && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-700 flex-1">
                        {t("subscriptions.updateAvailable", {
                            version: updateAvailable.latestVersion,
                        })}
                    </span>
                    <Button size="sm" onClick={handleApplyUpdate} disabled={applyingUpdate}>
                        {applyingUpdate ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                            <ArrowDownCircle className="w-3 h-3 mr-1" />
                        )}
                        {t("subscriptions.applyUpdate")}
                    </Button>
                </div>
            )}
            <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                    {t("subscriptions.updatePolicy")}
                </span>
                <Select value={policy} onValueChange={handlePolicyChange} disabled={updatingPolicy}>
                    <SelectTrigger className="w-[120px] h-8">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="AUTO">{t("subscriptions.policyAuto")}</SelectItem>
                        <SelectItem value="MANUAL">{t("subscriptions.policyManual")}</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <Button variant="ghost" size="sm" onClick={handleEject} disabled={ejecting} className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full">
                <Unlink className="w-3 h-3 mr-1" />
                {t("subscriptions.eject")}
            </Button>
        </div>
    );
}
